/**
 * Data hooks for the sale event hubs (web plan W0.15 / W3.4). Like the hooks
 * in useAppData.ts, these are the only way the sale pages reach Apollo.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { PUBLISHED_PROMOTIONS_RICH, SALE_PAGE, SALE_EVENTS_LIST } from '@/graphql/queries';
import {
  participantAlias,
  participantBrandsQuery,
  SALE_PARTICIPANT_BRAND,
} from '@/graphql/saleEvents';
import {
  saleEventPromotionsWhere,
  type BackendSaleWindow,
  type BackendSaleParticipant,
  type BackendSaleEventSummary,
} from '@/seo/saleEvents';
import { localeKey } from '@/seo/locales';
import { LIST_PAGE_SIZE } from '@/seo/urls';
import { isObjectId } from '@/utils/detailStatus';
import type { Locale } from '@/seo/locales';
import type { Pagination, Promotion } from '@/types';
import { queryErrorMessage } from './useAppData';
import { usePreloadedQuery } from './routePreload';
import { useRequestCountry } from './useRouteLocale';

/** The catalog lists' page size (web plan W0.11), so every list pages alike. */
export const SALE_OFFERS_PAGE_LIMIT = LIST_PAGE_SIZE;

export interface SaleOffersState {
  /**
   * False when the event has no tag id for this environment
   * (`VITE_SALE_TAG_IDS`): nothing is fetched and the page shows its empty
   * state. Never a fallback to the unfiltered feed.
   */
  enabled: boolean;
  /** Every offer loaded so far (pages accumulate). */
  items: Promotion[];
  /** A request is in flight. */
  loading: boolean;
  error: string | null;
  /** The backend's total for the event's tag, once a page has arrived. */
  totalRecords: number | null;
  /** More pages exist beyond the ones loaded. */
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

interface PromotionsData {
  publishedPromotions?: { pagination?: Pagination | null; promotions?: Promotion[] | null } | null;
}

/**
 * The event's live offers: `publishedPromotions` filtered to the event's tag
 * (upcoming and active promotions; the backend drops ended ones), a page at a
 * time. The component using it should be keyed by the event, so moving from
 * one event to another starts again at page 1.
 */
export function useSaleEventPromotions(
  tagId: string | null,
  limit = SALE_OFFERS_PAGE_LIMIT,
): SaleOffersState {
  const where = saleEventPromotionsWhere(tagId);
  const [page, setPage] = useState(1);
  // Offers of the pages before `page`, frozen when "more" was asked for. The
  // current page is read straight from the query, so its rows show in the
  // same render they arrive in (no empty frame before an effect copies them).
  const [earlier, setEarlier] = useState<readonly Promotion[]>([]);

  // The route's country once the URL move is live (web plan W2.1); left out before it.
  const country = useRequestCountry();
  const result = useQuery<PromotionsData>(PUBLISHED_PROMOTIONS_RICH, {
    skip: !where,
    variables: {
      options: { paginationOptions: { page, limit }, published: true, where, ...(country ? { country } : {}) },
    },
    // Retry goes back through `loading` instead of leaving the error up.
    notifyOnNetworkStatusChange: true,
  });

  const payload = result.data?.publishedPromotions;
  const current = payload?.promotions;
  const items = useMemo(() => {
    if (!current?.length) return [...earlier];
    const seen = new Set(earlier.map((p) => p.id));
    return [...earlier, ...current.filter((p) => !seen.has(p.id))];
  }, [earlier, current]);

  // While the next page loads, the last answer's pagination keeps "more" up.
  const pagination =
    payload?.pagination ?? result.previousData?.publishedPromotions?.pagination ?? null;
  const hasMore =
    !!pagination &&
    typeof pagination.currentPage === 'number' &&
    typeof pagination.totalPages === 'number' &&
    pagination.currentPage < pagination.totalPages;

  return {
    enabled: !!where,
    items,
    loading: !!where && result.loading,
    error: queryErrorMessage(result.error),
    totalRecords: pagination?.totalRecords ?? null,
    hasMore,
    // Only once the current page is in: a double click or a click during a
    // failed load never skips a page.
    loadMore: () => {
      if (!payload || result.loading || !hasMore) return;
      setEarlier(items);
      setPage((p) => p + 1);
    },
    refetch: () => {
      if (!where) return;
      result.refetch().catch(() => {});
    },
  };
}

type ParticipantBrandsData = Record<string, { id?: string | null; urlPath?: string | null } | null | undefined>;

/** A brand the backend confirmed as visible, with its canonical path (null → link by id). */
export interface VisibleBrand {
  id: string;
  urlPath: string | null;
}

/**
 * The brands, among `ids`, that the backend confirms are publicly visible
 * right now (keyed by id, with their `urlPath`), in one request
 * (`participantBrandsQuery`). An id the backend answers NOT_FOUND / GONE for
 * (draft, blocked, outside the country, deleted), an id that is not an
 * ObjectId, and every id while the answer is pending or after a failed
 * request are all simply absent: the caller cannot tell a blocked brand from
 * a draft through the public API, so it only shows what is confirmed.
 */
export function useVisibleBrands(ids: readonly string[]): ReadonlyMap<string, VisibleBrand> {
  const key = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id list's content
  const valid = useMemo(() => [...new Set(ids.filter(isObjectId))], [key]);
  const document = useMemo(() => participantBrandsQuery(valid.length), [valid.length]);
  const variables = useMemo(
    () => Object.fromEntries(valid.map((id, i) => [participantAlias(i), id])),
    [valid],
  );

  const result = useQuery<ParticipantBrandsData>(document ?? SALE_PARTICIPANT_BRAND, {
    skip: !document,
    variables,
    // One brand that is not visible fails only its own alias; keep the rest.
    errorPolicy: 'all',
  });

  const data = result.data;
  return useMemo(() => {
    const visible = new Map<string, VisibleBrand>();
    if (!data) return visible;
    valid.forEach((id, i) => {
      const brand = data[participantAlias(i)];
      if (brand?.id === id) visible.set(id, { id, urlPath: brand.urlPath ?? null });
    });
    return visible;
  }, [data, valid]);
}

// ── W3.4: salePage hook ───────────────────────────────────────────────────────

/** The shape of a `salePage` GraphQL response (B3.7a). */
export interface SalePageData {
  event: BackendSaleEventSummary;
  nextWindow?: BackendSaleWindow | null;
  live: boolean;
  previousWindow?: BackendSaleWindow | null;
  livePromotions: Promotion[];
  upcomingPromotions: Promotion[];
  previousParticipants: BackendSaleParticipant[];
  seo: { indexable: boolean; reason: string; robots: string; alternates?: ReadonlyArray<{ hreflang: string; url: string }> | null };
}

interface SalePageQueryResult {
  salePage?: SalePageData | null;
}

/**
 * Fetch the backend's `salePage` (B3.7a) for a single event hub.
 * Returns null data while loading or when the backend returns nothing; a
 * queryErrorMessage string on error (NOT_FOUND → "Sale Event Not Found").
 */
export function useSalePage(
  slug: string,
  locale?: string,
): { loading: boolean; error: string | null; data: SalePageData | null } {
  // The sale route module's server copy answers first (web plan W4.5).
  const result = usePreloadedQuery<SalePageQueryResult>(SALE_PAGE, {
    variables: { slug, locale },
    // Treat NOT_FOUND as an error so callers can decide between 404 and fallback.
    errorPolicy: 'all',
  });
  return {
    loading: result.loading,
    error: queryErrorMessage(result.error),
    data: result.data?.salePage ?? null,
  };
}

// ── W3.4: saleEventsList hook ─────────────────────────────────────────────────

/** One row from a `saleEvents` list response. */
export interface BackendSaleEventRow extends BackendSaleEventSummary {
  urlPath?: string | null;
}

interface SaleEventsListQueryResult {
  saleEvents?: BackendSaleEventRow[] | null;
}

/**
 * Fetch the published sale events for the calendar from the backend. The
 * backend already filters to the request's country; pass `locale` so the
 * url-path variables are locale-scoped. Returns null while loading.
 */
export function useSaleEventsList(
  locale: Locale,
): { loading: boolean; error: string | null; events: BackendSaleEventRow[] | null } {
  const country = useRequestCountry();
  // The calendar route module's server copy answers first (web plan W4.5).
  const result = usePreloadedQuery<SaleEventsListQueryResult>(SALE_EVENTS_LIST, {
    variables: {
      options: {
        ...(country ? { country } : {}),
        locale: localeKey(locale),
      },
    },
  });
  return {
    loading: result.loading,
    error: queryErrorMessage(result.error),
    events: result.data?.saleEvents ?? null,
  };
}
